import { CommonModule, NgOptimizedImage } from "@angular/common";
import { Component, inject, input, ViewChild } from "@angular/core";
import { FormsModule, NgForm, ReactiveFormsModule } from "@angular/forms";
import { RecaptchaFormsModule, RecaptchaModule } from "ng-recaptcha-2";
import {
  catchError,
  map,
  Observable,
  of,
  share,
  startWith,
  Subject,
  switchMap,
  tap,
} from "rxjs";
import {
  FORM_INITIAL_VALUE,
  FormType,
  FormValue,
  JobInfo,
  PostService,
} from "./post.service";
import { environment } from "../../../enviroments/enviroment";

const FILE_UPLOAD_PLACEHOLDER = "Additional files (optional)";
type ContactFormConfig = {
  name: string;
  linkedIn: string;
  imgUrl: string;
  altDescription: string;
  position: string;
  mailTo: string;
  titleSlogan: string;
  slogan?: string;
  errorMessage?: string;
  successMessage?: string;
  formType: FormType;
};

@Component({
  selector: "app-contact-form",
  templateUrl: "./contact-form.component.html",
  styleUrls: ["./contact-form.component.scss"],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    CommonModule,
    NgOptimizedImage,
  ],
})
export class ContactFormComponent {
  public readonly config = input.required<ContactFormConfig>();
  public readonly jobInfo = input<JobInfo | undefined>();

  @ViewChild("contactForm") public contactForm?: NgForm;

  public formValue = { ...FORM_INITIAL_VALUE };
  public siteKey = environment.captchaKey;

  #isLoading$ = new Subject<FormValue>();
  #isErrorSubject = new Subject<void>();
  #isError$ = this.#isErrorSubject.asObservable();
  #isSuccessSubject = new Subject<void>();
  #isSuccess$ = this.#isSuccessSubject.asObservable();
  #postService = inject(PostService);

  #selectedFile: File | null = null;
  public isLoading$ = this.createLoadingStream();
  public isErrorVisible$ = this.toVisibleStream(this.#isError$);
  public successResponse$ = this.toVisibleStream(this.#isSuccess$);
  public fileUploadText = FILE_UPLOAD_PLACEHOLDER;

  private createLoadingStream(): Observable<boolean> {
    return this.#isLoading$.pipe(
      switchMap((res) => {
        const jobInfo = this.jobInfo();
        if (jobInfo) {
          res.jobTitle = jobInfo.jobTitle;
          res.jobUrl = jobInfo.jobUrl;
        }

        return this.#postService
          .sendMessage(res, this.#selectedFile, this.config().formType)
          .pipe(
            tap((res) => {
              if (res) {
                this.#isSuccessSubject.next();
              }
            }),
            catchError(() => {
              this.#isErrorSubject.next();
              return of(false);
            }),
            tap((isLoading) => {
              if (!isLoading) {
                this.contactForm?.resetForm();
                this.fileUploadText = FILE_UPLOAD_PLACEHOLDER;
              }
            }),
            startWith(true),
          );
      }),
      share(),
    );
  }

  public onFormSubmit() {
    if (this.contactForm && this.contactForm.valid) {
      this.#isLoading$.next(this.formValue);
    }
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (file) {
      this.#selectedFile = file;
      this.fileUploadText = file.name;
    }
  }

  private toVisibleStream(obs$: Observable<unknown>): Observable<boolean> {
    return obs$.pipe(map(() => true));
  }
}
